import React, { useEffect, useRef, useCallback } from 'react';
import Konva from 'konva';
import { findIndexWithTickets, parseLayoutJson, stringToColour } from '../seating/utils/seatingUtils';

const COLORS = {
  SOLD: '#374151',
  HOLD: '#f59e0b',
  BLOCKED_FILL: '#ffedd5', // acik turuncu
  BLOCKED_STROKE: '#c2410c', // koyu turuncu border
  CONFLICT: '#dc2626',
  SYNC: '#2563eb', // 2. yer senkron
  SELECTED_STROKE: '#2563eb',
};

/**
 * Read-only Konva layout viewer with sold / hold / conflict overlays + seat click.
 * selectedSeatId değişince o koltuğa scroll eder (çakışma listesinden navigasyon).
 */
const SeatOpsViewer = ({
  layoutJSON,
  soldSeatIds = [],
  holdSeatIds = [],
  blockedSeatIds = [],
  conflictSeatIds = [],
  syncSeatIds = [],
  selectedSeatId = null,
  onSeatClick,
  width = Math.min(window.innerWidth * 0.55, 900),
  height = Math.min(window.innerHeight * 0.7, 640),
}) => {
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const layerRef = useRef(null);
  const soldSet = useRef(new Set(soldSeatIds));
  const holdSet = useRef(new Set(holdSeatIds));
  const blockedSet = useRef(new Set(blockedSeatIds));
  const conflictSet = useRef(new Set(conflictSeatIds));
  const syncSet = useRef(new Set(syncSeatIds));

  useEffect(() => {
    soldSet.current = new Set(soldSeatIds);
    holdSet.current = new Set(holdSeatIds);
    blockedSet.current = new Set(blockedSeatIds);
    conflictSet.current = new Set(conflictSeatIds);
    syncSet.current = new Set(syncSeatIds);
  }, [soldSeatIds, holdSeatIds, blockedSeatIds, conflictSeatIds, syncSeatIds]);

  const getSeatLabel = (group) => {
    try {
      const text = group.findOne?.('Text') || group.find?.('Text')?.[0];
      return text?.attrs?.text != null ? String(text.attrs.text) : null;
    } catch {
      return null;
    }
  };

  const findGroupByLabel = useCallback((label) => {
    const layer = layerRef.current;
    if (!layer || label == null) return null;
    const groups = layer.find('Group') || [];
    for (const group of groups) {
      if (getSeatLabel(group) === String(label)) return group;
    }
    return null;
  }, []);

  const scrollToSeat = useCallback((label) => {
    const scrollEl = scrollRef.current;
    const group = findGroupByLabel(label);
    if (!scrollEl || !group) return;

    try {
      const box = group.getClientRect({ skipTransform: false });
      const targetLeft = Math.max(0, box.x + box.width / 2 - scrollEl.clientWidth / 2);
      const targetTop = Math.max(0, box.y + box.height / 2 - scrollEl.clientHeight / 2);
      scrollEl.scrollTo({
        left: targetLeft,
        top: targetTop,
        behavior: 'smooth',
      });
    } catch (e) {
      console.warn('scrollToSeat failed', e);
    }
  }, [findGroupByLabel]);

  const paintSeat = useCallback((group, highlightedLabel) => {
    const label = getSeatLabel(group);
    if (!label || !group.children?.[0]) return;

    const rect = group.children[0];
    const text = group.children[1];
    const productId = group.attrs?.ticket;

    if (conflictSet.current.has(label)) {
      rect.fill(COLORS.CONFLICT);
      if (text) text.fill('white');
      rect.stroke('black');
      rect.strokeWidth(1.5);
    } else if (syncSet.current.has(label)) {
      rect.fill(COLORS.SYNC);
      if (text) text.fill('white');
      rect.stroke('black');
      rect.strokeWidth(1.5);
    } else if (blockedSet.current.has(label)) {
      rect.fill(COLORS.BLOCKED_FILL);
      if (text) text.fill('#9a3412');
      rect.stroke(COLORS.BLOCKED_STROKE);
      rect.strokeWidth(2.5);
    } else if (soldSet.current.has(label)) {
      rect.fill(COLORS.SOLD);
      if (text) text.fill('white');
      rect.stroke('black');
      rect.strokeWidth(1.5);
    } else if (holdSet.current.has(label)) {
      rect.fill(COLORS.HOLD);
      if (text) text.fill('black');
      rect.stroke('black');
      rect.strokeWidth(1.5);
    } else if (productId) {
      rect.fill(stringToColour(String(productId)));
      if (text) text.fill('white');
      rect.stroke('black');
      rect.strokeWidth(1.5);
    } else {
      rect.fill('#fff');
      if (text) text.fill('black');
      rect.stroke('black');
      rect.strokeWidth(1.5);
    }

    if (highlightedLabel && label === highlightedLabel) {
      rect.stroke(COLORS.SELECTED_STROKE);
      rect.strokeWidth(4);
      try {
        group.moveToTop();
      } catch {
        // ignore
      }
    }
  }, []);

  const applyOverlays = useCallback(
    (highlightedLabel) => {
      const layer = layerRef.current;
      const stage = stageRef.current;
      if (!layer) return;
      layer.find('Group').forEach((group) => paintSeat(group, highlightedLabel));
      stage?.batchDraw();
    },
    [paintSeat],
  );

  useEffect(() => {
    if (!layoutJSON || !containerRef.current) return undefined;

    containerRef.current.innerHTML = '';

    let stageJSON;
    try {
      stageJSON = parseLayoutJson(layoutJSON);
    } catch (e) {
      console.error('SeatOpsViewer layout parse failed', e);
      return undefined;
    }

    const stage = Konva.Node.create(stageJSON, containerRef.current);
    stageRef.current = stage;

    const layerIndex = findIndexWithTickets(stageJSON);
    const layer = stage.find('Layer')[layerIndex];
    layerRef.current = layer;

    try {
      const groups = stage.find('Group') || [];
      let maxX = Number(stageJSON?.attrs?.width) || 0;
      let maxY = Number(stageJSON?.attrs?.height) || 0;
      groups.forEach((group) => {
        const box = group.getClientRect({ skipTransform: false });
        maxX = Math.max(maxX, box.x + box.width);
        maxY = Math.max(maxY, box.y + box.height);
      });
      stage.width(Math.ceil(maxX) + 20);
      stage.height(Math.ceil(maxY) + 40);
    } catch {
      // keep attrs
    }

    layer.find('Group').forEach((group) => {
      group.draggable(false);
      group.off('click');
      group.off('tap');
      group.on('click tap', () => {
        const label = getSeatLabel(group);
        if (!label) return;
        onSeatClick?.({
          seatId: label,
          productId: group.attrs?.ticket || null,
          group,
        });
      });
    });

    applyOverlays(selectedSeatId);
    stage.draw();

    if (selectedSeatId) {
      requestAnimationFrame(() => scrollToSeat(selectedSeatId));
    }

    return () => {
      try {
        stage.destroy();
      } catch {
        // ignore
      }
      stageRef.current = null;
      layerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutJSON]);

  useEffect(() => {
    applyOverlays(selectedSeatId);
    if (selectedSeatId) {
      requestAnimationFrame(() => scrollToSeat(selectedSeatId));
    }
  }, [soldSeatIds, holdSeatIds, blockedSeatIds, conflictSeatIds, syncSeatIds, selectedSeatId, applyOverlays, scrollToSeat]);

  return (
    <div
      ref={scrollRef}
      style={{
        overflow: 'auto',
        width,
        height,
        border: '1px solid #ccc',
        background: '#f8fafc',
      }}
    >
      <div ref={containerRef} />
    </div>
  );
};

export default SeatOpsViewer;
